import { GameStats } from '../components/GameStats'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { getGameStats, getPlayedModes, getDailyTimeStats } from '../utils/storage'
import { getPlayerProgression } from '../utils/playerProgression'
import { useEffect, useState, lazy, Suspense } from 'react'
import { trackPageView } from '../services/gameAnalytics'
import { trackEvent } from '../services/hotjar'
import { Leaderboard } from '../components/Leaderboard'
import { StreakCard } from '../components/StreakCard'
import { useStats } from '../contexts/StatsContext'
import { toGameStats } from '../utils/toGameStats'
import { GAME_MODES, DEFAULT_MODE } from '../constants/gameConfig'
import { WordLength } from '../types/game'

// Lazy-loaded: bundles Victory (with its D3 dependencies), which should
// only be fetched when a player actually visits the Statistics page.
const DailyTimeChart = lazy(() =>
  import('../components/DailyTimeChart').then((m) => ({
    default: m.DailyTimeChart,
  }))
)

export const StatisticsPage = () => {
  const navigate = useNavigate()
  // Streaks and stats come from the server (same on every device, and across a
  // signed-in account); this browser's own copy is only the fallback while the
  // server has not answered or can't be reached.
  const { data: serverStats, refresh } = useStats()
  const [localStats] = useState(() => getGameStats())
  const stats = serverStats ? toGameStats(serverStats) : localStats
  const [progression] = useState(() => getPlayerProgression())
  const [dailyTimeStats] = useState(() => getDailyTimeStats(7))
  const [leaderboardLength, setLeaderboardLength] = useState<WordLength>(() => {
    const playedModes = getPlayedModes()
    return playedModes[playedModes.length - 1] ?? DEFAULT_MODE
  })

  useEffect(() => {
    // Opening the page asks again, so a game finished elsewhere shows up.
    void refresh()
  }, [refresh])

  useEffect(() => {
    // Track statistics page view (once, from this browser's numbers, so the
    // server's later answer does not count as a second view)
    try {
      trackPageView('/statistics', {
        games_played: localStats.gamesPlayed,
        current_streak: localStats.currentStreak,
        max_streak: localStats.maxStreak,
      })

      trackEvent('statistics_page_viewed', {
        games_played: localStats.gamesPlayed,
        current_streak: localStats.currentStreak,
        max_streak: localStats.maxStreak,
        timestamp: Date.now(),
      })
    } catch (error) {
      console.error('Failed to track statistics page view:', error)
    }
  }, [localStats])

  const handleBackToModes = () => {
    trackEvent('back_to_modes_from_stats', {
      games_played: stats.gamesPlayed,
    })
    navigate('/zword')
  }

  // Calculate additional stats
  const totalDaysPlayed = Object.values(progression.daysPlayed).reduce(
    (sum, days) => sum + days,
    0
  )

  // Averaged over the games in the distribution: server win counts include
  // Anagram, but the distribution is Zword only.
  const distributionTotal = Object.values(stats.guessDistribution).reduce(
    (sum, count) => sum + count,
    0
  )
  const averageGuesses =
    distributionTotal > 0
      ? Object.entries(stats.guessDistribution).reduce(
        (sum, [guesses, count]) => sum + parseInt(guesses) * count,
        0
      ) / distributionTotal
      : 0

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 },
  }

  return (
    <div className="min-h-screen bg-[#121212] text-white">


      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="space-y-8"
        >
          <motion.div variants={item}>
            <StreakCard />
          </motion.div>

          {/* Global Leaderboard Section */}
          <motion.div
            variants={item}
            className="bg-[#1a1a1a] rounded-2xl p-6 border border-[#333] shadow-lg"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
              <div>
                <h3 className="text-2xl font-bold text-white">Leaderboard</h3>
                <p className="text-white/50 text-sm">Top verified solves for today's challenge</p>
              </div>
              <div className="flex bg-[#222] rounded-lg p-1 border border-white/5">
                {(Object.keys(GAME_MODES).map(Number) as WordLength[]).map((length) => (
                  <button
                    key={length}
                    onClick={() => setLeaderboardLength(length)}
                    className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${
                      leaderboardLength === length
                        ? 'bg-[#ed1c24] shadow-lg'
                        : 'text-white/40 hover:text-white'
                    }`}
                  >
                    {GAME_MODES[length].label}
                  </button>
                ))}
              </div>
            </div>

            <Leaderboard mode="daily" length={leaderboardLength} />

            <div className="mt-8 pt-6 border-t border-white/5 flex items-center gap-3 text-white/30 text-xs italic">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" /></svg>
            </div>
          </motion.div>

          {/* Main Statistics Card */}
          <motion.div
            variants={item}
            className="bg-[#1a1a1a] rounded-2xl p-6 border border-[#333] shadow-lg"
          >
            <h2 className="text-2xl font-bold mb-6 text-center">
              Game Statistics
            </h2>
            <GameStats stats={stats} />
          </motion.div>

          {/* Time Spent Solving */}
          <motion.div
            variants={item}
            className="bg-[#1a1a1a] rounded-2xl p-6 border border-[#333] shadow-lg"
          >
            <h2 className="text-2xl font-bold mb-2 text-center">
              Time Spent Solving
            </h2>
            <p className="text-sm text-[#888] text-center mb-6">
              Per day, last 7 days
            </p>
            <Suspense fallback={<div className="h-[220px]" />}>
              <DailyTimeChart data={dailyTimeStats} />
            </Suspense>
          </motion.div>

          {/* Additional Stats */}
          <motion.div variants={item} className="grid gap-6 md:grid-cols-2">
            {/* Performance Summary */}
            <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-[#333] shadow-lg">
              <h3 className="text-xl font-bold mb-4 text-white/90">
                Performance Summary
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/70">Total Days Played</span>
                  <span className="text-2xl font-bold text-[#ff6b6f]">
                    {totalDaysPlayed}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/70">Average Guesses</span>
                  <span className="text-2xl font-bold text-[#ff6b6f]">
                    {averageGuesses > 0 ? averageGuesses.toFixed(1) : '—'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/70">Best Streak</span>
                  <span className="text-2xl font-bold text-[#ff6b6f]">
                    {stats.maxStreak}
                  </span>
                </div>
              </div>
            </div>

            {/* Mode Progress */}
            <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-[#333] shadow-lg">
              <h3 className="text-xl font-bold mb-4 text-white/90">
                Mode Progress
              </h3>
              <div className="space-y-4">
                {Object.entries(progression.daysPlayed)
                  .sort(([a], [b]) => parseInt(b) - parseInt(a))
                  .map(([mode, days]) => (
                    <div
                      key={mode}
                      className="flex justify-between items-center"
                    >
                      <span className="text-white/70">{mode}-Letter Words</span>
                      <span className="text-2xl font-bold text-[#ff6b6f]">
                        {days} {days === 1 ? 'day' : 'days'}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </motion.div>

          {/* Achievements Section */}
          <motion.div
            variants={item}
            className="bg-[#1a1a1a] rounded-2xl p-6 border border-[#333] shadow-lg"
          >
            <h3 className="text-xl font-bold mb-4 text-white/90">
              Achievements
            </h3>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {/* First Game Achievement */}
              <div
                className={`p-4 rounded-lg border ${stats.gamesPlayed > 0
                  ? 'bg-gradient-to-br from-[#5c1216]/60 to-[#5c1216]/20 border-[#ed1c24]/30'
                  : 'bg-[#2a2a2a] border-[#444]'
                  }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className={`text-2xl ${stats.gamesPlayed > 0 ? 'text-[#ed1c24]' : 'text-white/30'}`}
                  >
                    🔥
                  </div>
                  <h4 className="font-semibold">First Steps</h4>
                </div>
                <p className="text-sm text-white/60">
                  {stats.gamesPlayed > 0
                    ? 'Completed your first game!'
                    : 'Play your first game'}
                </p>
              </div>

              {/* Streak Achievement */}
              <div
                className={`p-4 rounded-lg border ${stats.maxStreak >= 5
                  ? 'bg-gradient-to-br from-[#5c1216]/60 to-[#5c1216]/20 border-[#ed1c24]/30'
                  : 'bg-[#2a2a2a] border-[#444]'
                  }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className={`text-2xl ${stats.maxStreak >= 5 ? 'text-[#ed1c24]' : 'text-white/30'}`}
                  >
                    🔥
                  </div>
                  <h4 className="font-semibold">Hot Streak</h4>
                </div>
                <p className="text-sm text-white/60">
                  {stats.maxStreak >= 5
                    ? 'Reached a 5-day streak!'
                    : 'Play 5 days in a row'}
                </p>
              </div>

              {/* Dedication Achievement */}
              <div
                className={`p-4 rounded-lg border ${totalDaysPlayed >= 7
                  ? 'bg-gradient-to-br from-[#5c1216]/60 to-[#5c1216]/20 border-[#ed1c24]/30'
                  : 'bg-[#2a2a2a] border-[#444]'
                  }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className={`text-2xl ${totalDaysPlayed >= 7 ? 'text-[#ed1c24]' : 'text-white/30'}`}
                  >
                    🔥
                  </div>
                  <h4 className="font-semibold">Dedicated</h4>
                </div>
                <p className="text-sm text-white/60">
                  {totalDaysPlayed >= 7
                    ? 'Played for 7 days!'
                    : 'Play for 7 days total'}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Back to Game Button */}
          <motion.div variants={item} className="text-center">
            <button
              onClick={handleBackToModes}
              className="bg-gradient-to-r from-[#b01018] to-[#ed1c24] hover:from-[#c81a22] hover:to-[#ff3b42] text-white px-8 py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Continue Playing
            </button>
          </motion.div>
        </motion.div>
      </main>
    </div>
  )
}
